// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * A date and time, typed or picked, drawn in the language the app is in.
 *
 * **Why not `<input type="datetime-local">`.** Chrome and Safari draw that control in the
 * browser's own language whatever the page says, so an operator who switched the app to
 * English still saw 年 / 月 / 日 in the one field where a misread costs a wrong record. This
 * control's text, month names and weekdays all come from the app's locale.
 *
 * The value is the same string `datetime-local` produced — `YYYY-MM-DDTHH:mm`, local time,
 * `""` for empty — so a caller reads it with `new Date(value)` as before. Text that is not a
 * real date reports `null`, which the caller treats as "cannot save yet" rather than
 * silently dropping it for the time of submission.
 *
 * The text typed is the field's own state, read once from `value` when it mounts: the form
 * holding it unmounts on save or cancel, so there is no outside reset to follow.
 *
 * Typing is the quick path (the format is in the placeholder); the calendar button opens a
 * month grid with an hour and minute below it, and "now" / "clear".
 */

import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarIcon } from "./icons";

interface Props {
  id: string;
  /** `YYYY-MM-DDTHH:mm`, or `""` for empty. */
  value: string;
  /** The new value, or `null` while the text typed is not a valid date and time. */
  onChange: (value: string | null) => void;
  describedBy?: string;
}

interface Parts {
  year: number;
  month: number; // 0-11
  day: number;
  hour: number;
  minute: number;
}

const TEXT = /^\s*(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T]+(\d{1,2}):(\d{1,2}))?\s*$/;

export function DateTimeField({ id, value, onChange, describedBy }: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [text, setText] = useState(() => display(value));
  const [invalid, setInvalid] = useState(false);
  const [open, setOpen] = useState(false);
  const current = parse(value);
  const [shown, setShown] = useState(() => monthOf(current ?? partsOf(new Date())));
  const root = useRef<HTMLDivElement>(null);
  const popupId = useId();
  const errorId = `${id}-invalid`;

  useEffect(() => {
    if (!open) return;
    function onPointer(event: MouseEvent) {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, [open]);

  function commit(next: Parts | null) {
    const nextValue = toValue(next);
    setText(display(nextValue));
    setInvalid(false);
    onChange(nextValue);
  }

  function onType(raw: string) {
    setText(raw);
    if (raw.trim() === "") {
      setInvalid(false);
      onChange("");
      return;
    }
    const parts = parse(raw);
    // Judged on every keystroke for the value, but only called out once the field is left:
    // "2026-10-1" half way through typing is not an error yet.
    onChange(parts ? toValue(parts) : null);
    if (parts) setInvalid(false);
  }

  function openPicker() {
    setShown(monthOf(current ?? partsOf(new Date())));
    setOpen((was) => !was);
  }

  function pickDay(day: number) {
    const base = current ?? { ...partsOf(new Date()) };
    commit({ ...base, year: shown.year, month: shown.month, day });
  }

  function pickTime(field: "hour" | "minute", amount: number) {
    const base = current ?? partsOf(new Date());
    commit({ ...base, [field]: amount });
  }

  const monthTitle = new Intl.DateTimeFormat(locale, { year: "numeric", month: "long" }).format(
    new Date(shown.year, shown.month, 1),
  );
  // Monday first, named in the app's language: 2024-01-01 was a Monday.
  const weekdays = Array.from({ length: 7 }, (_, index) =>
    new Intl.DateTimeFormat(locale, { weekday: "short" }).format(new Date(2024, 0, 1 + index)),
  );
  const leading = (new Date(shown.year, shown.month, 1).getDay() + 6) % 7;
  const days = new Date(shown.year, shown.month + 1, 0).getDate();
  const today = partsOf(new Date());

  return (
    <div
      className="datetime-field"
      ref={root}
      data-own-escape={open ? "" : undefined}
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) setOpen(false);
      }}
    >
      <div className="datetime-input">
        <input
          id={id}
          className="mono"
          autoComplete="off"
          inputMode="numeric"
          placeholder={t("datetime.placeholder")}
          aria-describedby={[describedBy, invalid ? errorId : null].filter(Boolean).join(" ")}
          aria-invalid={invalid || undefined}
          value={text}
          onChange={(event) => onType(event.target.value)}
          onBlur={() => {
            if (text.trim() === "") return;
            const parts = parse(text);
            if (parts) setText(display(toValue(parts)));
            else setInvalid(true);
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown" && event.altKey) openPicker();
          }}
        />
        <button
          type="button"
          className="datetime-toggle tooltip-trigger"
          aria-label={t("datetime.open")}
          data-tooltip={t("datetime.open")}
          aria-expanded={open}
          aria-controls={popupId}
          onClick={openPicker}
        >
          <CalendarIcon size={16} />
        </button>
      </div>
      {invalid && (
        <p className="hint hint-error" id={errorId} role="alert">
          {t("datetime.invalid")}
        </p>
      )}
      {open && (
        <div className="datetime-popup" id={popupId} role="dialog" aria-label={t("datetime.open")}>
          <div className="datetime-month">
            <button
              type="button"
              aria-label={t("datetime.previousMonth")}
              onClick={() => setShown(shift(shown, -1))}
            >
              ‹
            </button>
            <strong aria-live="polite">{monthTitle}</strong>
            <button
              type="button"
              aria-label={t("datetime.nextMonth")}
              onClick={() => setShown(shift(shown, 1))}
            >
              ›
            </button>
          </div>
          <div className="datetime-grid" role="grid">
            {weekdays.map((name) => (
              <span key={name} className="datetime-weekday" role="columnheader">
                {name}
              </span>
            ))}
            {Array.from({ length: leading }, (_, index) => (
              <span key={`blank-${index}`} />
            ))}
            {Array.from({ length: days }, (_, index) => {
              const day = index + 1;
              const selected =
                current?.year === shown.year &&
                current.month === shown.month &&
                current.day === day;
              const isToday =
                today.year === shown.year && today.month === shown.month && today.day === day;
              return (
                <button
                  key={day}
                  type="button"
                  role="gridcell"
                  aria-selected={selected}
                  aria-label={new Intl.DateTimeFormat(locale, { dateStyle: "long" }).format(
                    new Date(shown.year, shown.month, day),
                  )}
                  className={[
                    "datetime-day",
                    selected ? "is-selected" : "",
                    isToday ? "is-today" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => pickDay(day)}
                >
                  {day}
                </button>
              );
            })}
          </div>
          <div className="datetime-time">
            <label>
              {t("datetime.hour")}
              <select
                value={current?.hour ?? ""}
                onChange={(event) => pickTime("hour", Number(event.target.value))}
              >
                {current === null && <option value="">--</option>}
                {Array.from({ length: 24 }, (_, hour) => (
                  <option key={hour} value={hour}>
                    {pad(hour)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t("datetime.minute")}
              <select
                value={current?.minute ?? ""}
                onChange={(event) => pickTime("minute", Number(event.target.value))}
              >
                {current === null && <option value="">--</option>}
                {Array.from({ length: 60 }, (_, minute) => (
                  <option key={minute} value={minute}>
                    {pad(minute)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="datetime-actions">
            <button type="button" onClick={() => commit(null)}>
              {t("datetime.clear")}
            </button>
            <button type="button" onClick={() => commit(partsOf(new Date()))}>
              {t("datetime.now")}
            </button>
            <button type="button" className="button-primary" onClick={() => setOpen(false)}>
              {t("datetime.done")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The zone a typed time is read in — the browser's, since the time typed carries none —
 * spelled out under the field, so a time entered at one site is not taken for another's.
 */
export function timeZoneLabel(): { zone: string; offset: string } {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const minutes = -new Date().getTimezoneOffset();
  const sign = minutes < 0 ? "-" : "+";
  const hours = String(Math.floor(Math.abs(minutes) / 60)).padStart(2, "0");
  const rest = String(Math.abs(minutes) % 60).padStart(2, "0");
  return { zone, offset: `UTC${sign}${hours}:${rest}` };
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

function partsOf(date: Date): Parts {
  return {
    year: date.getFullYear(),
    month: date.getMonth(),
    day: date.getDate(),
    hour: date.getHours(),
    minute: date.getMinutes(),
  };
}

/** `2026-10-1 8:30`, `2026-10-01T08:30`, or a bare date (read as 00:00); `null` if not real. */
export function parse(raw: string): Parts | null {
  const match = TEXT.exec(raw);
  if (!match) return null;
  const number = (index: number) => Number(match[index] ?? 0);
  const [year, month, day, hour, minute] = [number(1), number(2), number(3), number(4), number(5)];
  const parts = { year, month: month - 1, day, hour, minute };
  const date = new Date(year, month - 1, day, hour, minute);
  // 2026-02-30 rolls over to March in Date; a real date comes back as it went in.
  const real = hour < 24 && minute < 60 && JSON.stringify(partsOf(date)) === JSON.stringify(parts);
  return real ? parts : null;
}

function toValue(parts: Parts | null): string {
  if (!parts) return "";
  return `${parts.year}-${pad(parts.month + 1)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`;
}

function display(value: string): string {
  return value.replace("T", " ");
}

function monthOf(parts: Parts): { year: number; month: number } {
  return { year: parts.year, month: parts.month };
}

function shift(shown: { year: number; month: number }, by: number) {
  const date = new Date(shown.year, shown.month + by, 1);
  return { year: date.getFullYear(), month: date.getMonth() };
}
